import { upperCase } from "lodash";
import { mapPrice } from "../common/mapPrice";
import { mapClientName } from "../common/mapClientName";
import { mapUnixDateToLocaleDate } from "../common/mapUnixDateToLocaledate";
import { getProviderName } from "@/utils.js";
import { HELD_AWAY, TRADABLE, CLOSED } from "@/account_access_types";
import { getProviderNameFromStaticMap, getTxSource } from "@/static_contents_utils";
import { DEFAULT_CURRENCY_MIN_DECIMALS } from "@/filters";
import { COST_BASIS_AMOUNT_MAX_DECIMALS } from "@/views/shared/tabbed-transactions-card/tabbed-table-constants";
import {
    TAX_TAG_UNKNOWN,
    TAX_TAG_CONTRIBUTION,
    TAX_TAG_TRANSFER_IN,
    TAX_TAG_CONVERSION,
    TAX_TAG_ROLLOVER,
    TAX_TAG_LATE_ROLLOVER,
    TAX_TAG_POSTPONED,
    TAX_TAG_RECHARACTERIZATION,
    TAX_TAG_DISTRIBUTION,
    TAX_TAG_TRANSFER_OUT,
    TAX_TAG_CONVERSION_OUT
} from "@/views/Account Management/IRA Inflow Outflow/ira-inflow-outflow-const";
import {TAX_TAGGING_ENABLED} from "@/env";

export const mapTransactionsDtoToModel = (transactionsDto, providers) => {
    const priceFloat = parseFloat(transactionsDto.transaction_usd_price) || 0;
    const priceOfTransaction = mapPrice(priceFloat, DEFAULT_CURRENCY_MIN_DECIMALS, COST_BASIS_AMOUNT_MAX_DECIMALS);
    const amountAsDecimal = mapAmount(transactionsDto.transaction_amount, transactionsDto.transaction_symbol);
    const unitPrice = mapPrice(transactionsDto.transaction_unit_price);
    const status = mapAccountAccessStatus(transactionsDto.status);
    return {
        id: transactionsDto.id,
        first_name: transactionsDto.first_name,
        last_name: transactionsDto.last_name,
        transacted: mapUnixDateToLocaleDate(transactionsDto.transacted_date),
        settled: mapUnixDateToLocaleDate(transactionsDto.settled_date),
        client_name: mapClientName(transactionsDto.first_name, transactionsDto.last_name),
        account_id: transactionsDto.account_id,
        accountType: transactionsDto.account_type_text,
        account_holder_id: transactionsDto.client_id,
        account_groups: transactionsDto.rep_code,
        firmName: transactionsDto.firm_name,
        details: mapTransactionDetail(
            upperCase(transactionsDto.transaction_action),
            amountAsDecimal,
            transactionsDto.transaction_symbol,
            unitPrice),
        type: upperCase(transactionsDto.transaction_action),
        transactionAction: transactionsDto.transaction_action,
        symbol: transactionsDto.transaction_symbol,
        quantity: amountAsDecimal,
        rawQuantity: transactionsDto.transaction_amount,
        amount: priceOfTransaction,
        provider_id: getProviderNameFromStaticMap(transactionsDto.custodian_id),
        custodian: getProviderName(transactionsDto.custodian_id, providers),
        unitPrice: unitPrice,
        heldAway: status,
        fee_amount: transactionsDto.fee_amount,
        numberOfOccurrences: transactionsDto.number_of_occurrences,
        showCostBasisUpdate: transactionsDto.is_cost_basis_updateable,
        enableEditTaxTag: transactionsDto.is_tax_tag_updateable,
        source: getTxSource(transactionsDto.flags, transactionsDto.custodian_id),
        accountTypeId: transactionsDto.account_type_id,
        accountTypeText: transactionsDto.account_type_text,
        showTaxTagging: transactionsDto.is_tax_tag_updateable && TAX_TAGGING_ENABLED,
        taxTagId: transactionsDto.tax_tag,
        taxTag: mapTaxTag(transactionsDto.tax_tag),
        taxYear: mapTaxYear(transactionsDto.tax_year),
        tagStatus: mapTagStatus(transactionsDto.is_tax_tag_confirmed),
    };
};

export const mapIraTransactionsDtoToModel = (transactionsDto, providers) => {
    const priceFloat = parseFloat(transactionsDto.transaction_usd_price) || 0;
    const priceOfTransaction = mapPrice(priceFloat, DEFAULT_CURRENCY_MIN_DECIMALS, COST_BASIS_AMOUNT_MAX_DECIMALS);
    const amountAsDecimal = mapAmount(transactionsDto.transaction_amount, transactionsDto.transaction_symbol);
    const unitPrice = mapPrice(transactionsDto.transaction_unit_price);
    const status = mapAccountAccessStatus(transactionsDto.status);
    return {
        id: transactionsDto.id,
        transacted: mapUnixDateToLocaleDate(transactionsDto.transacted_date),
        settled: mapUnixDateToLocaleDate(transactionsDto.settled_date),
        client_name: mapClientName(transactionsDto.first_name, transactionsDto.last_name),
        account_id: transactionsDto.account_id,
        accountType: transactionsDto.account_type_text,
        account_holder_id: transactionsDto.client_id,
        account_groups: transactionsDto.rep_code,
        details: mapTransactionDetail(
            upperCase(transactionsDto.transaction_action),
            amountAsDecimal,
            transactionsDto.transaction_symbol,
            unitPrice),
        type: upperCase(transactionsDto.transaction_action),
        transactionAction: transactionsDto.transaction_action,
        symbol: transactionsDto.transaction_symbol,
        quantity: amountAsDecimal,
        rawQuantity: transactionsDto.transaction_amount,
        amount: priceOfTransaction,
        provider_id: getProviderNameFromStaticMap(transactionsDto.custodian_id),
        custodian: getProviderName(transactionsDto.custodian_id, providers),
        unitPrice: unitPrice,
        heldAway: status,
        fee_amount: transactionsDto.fee_amount,
        numberOfOccurrences: transactionsDto.number_of_occurrences,
        showCostBasisUpdate: transactionsDto.is_cost_basis_updateable,
        enableEditTaxTag: transactionsDto.is_tax_tag_updateable && TAX_TAGGING_ENABLED,
        source: getTxSource(transactionsDto.flags, transactionsDto.custodian_id),
        accountTypeId: transactionsDto.account_type_id,
        accountTypeText: transactionsDto.account_type_text,
        taxTag: mapTaxTag(transactionsDto.tax_tag),
        taxYear: mapTaxYear(transactionsDto.tax_year),
        tagStatus: mapTagStatus(transactionsDto.is_tax_tag_confirmed),
    };
};

const mapTaxTag = (taxTagId) => {
    switch (taxTagId) {
        case TAX_TAG_UNKNOWN:
            return "";
        case TAX_TAG_CONTRIBUTION:
            return "Contribution";
        case TAX_TAG_TRANSFER_IN:
            return "Transfer In";
        case TAX_TAG_CONVERSION:
            return "Conversion";
        case TAX_TAG_ROLLOVER:
            return "Rollover";
        case TAX_TAG_LATE_ROLLOVER:
            return "Late Rollover";
        case TAX_TAG_POSTPONED:
            return "Postponed";
        case TAX_TAG_RECHARACTERIZATION:
            return "Recharacterization";
        case TAX_TAG_DISTRIBUTION:
            return "Distribution";
        case TAX_TAG_TRANSFER_OUT:
            return "Transfer Out";
        case TAX_TAG_CONVERSION_OUT:
            return "Conversion Out";
        default:
            return ""; // Unknown
    }
};

const mapTaxYear = (taxDate) => {
    if (!taxDate) {
        return "";
    }
    const taxDateObj = new Date(taxDate);
    return taxDateObj.getUTCFullYear().toString();
}

const mapTagStatus = (isConfirmed) => {
    return isConfirmed == null ? "" : (isConfirmed ? "Confirmed" : "Unconfirmed");
}

const mapAmount = (amount, symbol) => {
    switch(symbol) {
        case "USD":
            return amount.toFixed(2);
        case "ETH":
            return amount.toFixed(6);
        case "BTC":
            return amount.toFixed(8);
        default:
            return amount;
    }
};

const mapTransactionDetail = (action, amount, currencyAmount, price) => {
    return `${action} ${amount} ${currencyAmount} at ${price}`;
};

const mapAccountAccessStatus = (accessStatus) => {
    if (accessStatus.is_account_closed) {
        return CLOSED;
    }

    return accessStatus.is_account_held_away ? HELD_AWAY : TRADABLE;
};